import React, { useState } from "react";
import { Phone, MessageCircle, MapPin, Clock, Mail } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { BRAND } from "../lib/brand";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone number");
      return;
    }
    toast.success("Thanks! We'll ring you back shortly.");
    setForm({ name: "", phone: "", message: "" });
  };

  return (
    <div data-testid="contact-page" className="pb-32">
      <PageHero
        testid="contact-hero"
        eyebrow="Let's talk kukkad"
        title="Get In"
        accent="Touch"
        subtitle="Order direct, book a table, or just tell us your favourite dish. We're all ears (and drumsticks)."
      />

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-14 grid lg:grid-cols-2 gap-12">
        {/* Info + order options */}
        <Reveal>
          <div className="grid sm:grid-cols-2 gap-4">
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="group bg-orange text-primary-foreground rounded-lg p-6 hover:scale-[1.02] transition-transform"
            >
              <MessageCircle className="w-8 h-8" />
              <p className="font-heading uppercase text-2xl mt-4 tracking-tight">WhatsApp</p>
              <p className="text-sm opacity-90 mt-1">Fastest way to order direct</p>
              <p className="font-bold mt-2">{BRAND.phoneDisplay}</p>
            </a>
            <a
              href={`tel:+91${BRAND.phone}`}
              data-testid="contact-call"
              className="group bg-card border border-border rounded-lg p-6 hover:border-orange transition-colors"
            >
              <Phone className="w-8 h-8 text-orange" />
              <p className="font-heading uppercase text-2xl mt-4 tracking-tight">Call Us</p>
              <p className="text-sm text-cream/60 mt-1">Mon–Sun, 11am–11pm</p>
              <p className="font-bold mt-2">{BRAND.phoneDisplay}</p>
            </a>
          </div>

          <div className="flex gap-4 mt-4">
            <a href={BRAND.zomato} target="_blank" rel="noreferrer" data-testid="contact-zomato" className="flex-1 text-center bg-card border border-border rounded-lg py-4 font-bold uppercase hover:border-orange hover:text-orange transition-colors">
              Order on Zomato
            </a>
            <a href={BRAND.swiggy} target="_blank" rel="noreferrer" data-testid="contact-swiggy" className="flex-1 text-center bg-card border border-border rounded-lg py-4 font-bold uppercase hover:border-orange hover:text-orange transition-colors">
              Order on Swiggy
            </a>
          </div>

          <div className="mt-6 space-y-4 text-cream/80">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-orange mt-0.5 shrink-0" />
              <div><p className="font-bold text-cream">Visit / Dine-in</p><p>{BRAND.location}</p></div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-orange mt-0.5 shrink-0" />
              <div><p className="font-bold text-cream">Hours</p><p>Everyday · 11:00 AM – 11:00 PM</p></div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-orange mt-0.5 shrink-0" />
              <div><p className="font-bold text-cream">Email</p><p>hello@thekukkadco.com</p></div>
            </div>
          </div>

          <div className="mt-6 rounded-lg overflow-hidden border border-border h-56">
            <iframe
              title="The Kukkad Co. Location"
              src="https://www.google.com/maps?q=Sector+62+Noida&output=embed"
              className="w-full h-full grayscale contrast-125"
              loading="lazy"
            />
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="bg-card border border-border rounded-lg p-7 sm:p-8">
            <h2 className="font-heading uppercase text-3xl tracking-tight">Drop us a line</h2>
            <p className="text-cream/60 mt-1">Catering, feedback or a table for tonight — we reply fast.</p>

            <div className="space-y-5 mt-6">
              <div>
                <Label htmlFor="c-name" className="uppercase text-xs tracking-widest text-cream/60">Name</Label>
                <Input
                  id="c-name"
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="mt-2 bg-brown-deep border-border text-cream focus-visible:ring-orange"
                />
              </div>
              <div>
                <Label htmlFor="c-phone" className="uppercase text-xs tracking-widest text-cream/60">Phone</Label>
                <Input
                  id="c-phone"
                  data-testid="contact-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="10-digit mobile"
                  className="mt-2 bg-brown-deep border-border text-cream focus-visible:ring-orange"
                />
              </div>
              <div>
                <Label htmlFor="c-msg" className="uppercase text-xs tracking-widest text-cream/60">Message</Label>
                <Textarea
                  id="c-msg"
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us what you need…"
                  rows={5}
                  className="mt-2 bg-brown-deep border-border text-cream focus-visible:ring-orange"
                />
              </div>
              <Button
                type="submit"
                data-testid="contact-submit"
                className="w-full bg-orange text-primary-foreground font-bold uppercase text-base py-6 hover:scale-[1.02] transition-transform"
              >
                Send Message
              </Button>
            </div>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
